import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShare,
    faCheck,
    faUsers,
    faMoneyBillWave,
    faCalendarAlt,
    faCrown,
} from '@fortawesome/free-solid-svg-icons';
import { EventWithId } from '../../hooks/useEvents';
import { useEventUsersCache } from '../../hooks/useEventUsersCache';
import { useTranslation } from 'react-i18next';
import { useFormatCurrency } from '../../hooks/useFormatCurrency';
import { resolveParticipantNames } from '../../utils/helpers';

interface FeaturedEventCardProps {
    event: EventWithId;
    copiedEventId: string | null;
    onShareLink: (eventId: string) => void;
}

/**
 * The newest still-open event, given a band of its own above the grid.
 *
 * It has to read as "this one is different" before any of its text is read: it is
 * display-sized type well past anything in the grid, sitting on a wash of indigo
 * light enough that the text on it stays ordinary dark-on-light. The card it used
 * to be — a full-bleed indigo→teal gradient with a radial wash over it and white
 * text — shouted loudly enough that the rest of the page read as background, which
 * is more emphasis than one row of a list needs.
 *
 * What is deliberately absent is any second marker: a caption, a badge or a status
 * dot explaining the highlight would each be one more thing competing with the
 * name they were meant to point at.
 */
const FeaturedEventCard: React.FC<FeaturedEventCardProps> = ({
                                                                 event,
                                                                 copiedEventId,
                                                                 onShareLink,
                                                             }) => {
    const { t, i18n } = useTranslation();
    const { formatCurrency } = useFormatCurrency();
    const { eventUsers } = useEventUsersCache(event.id);

    const isCopied = copiedEventId === event.id;

    // The whole card is a link, so the one button inside it has to stop the
    // navigation as well as the bubble — same as the grid cards below.
    const handleShareClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onShareLink(event.id);
    };

    const participantNames = useMemo(
        () => resolveParticipantNames(event.meta.participants, eventUsers),
        [event.meta.participants, eventUsers]
    );

    const participantIds = Object.keys(participantNames);
    const expenseCount = event.expenses ? Object.keys(event.expenses).length : 0;
    const total = useMemo(() => (
        Object.values(event.expenses || {}).reduce((sum, expense) => sum + expense.amount, 0)
    ), [event.expenses]);

    const sortedParticipantIds = useMemo(() => (
        [...participantIds].sort((a, b) => {
            if (a === event.meta.createdBy) return -1;
            if (b === event.meta.createdBy) return 1;
            return 0;
        })
    ), [participantIds, event.meta.createdBy]);

    return (
        <section className="mb-8" aria-label={t('myEvents.featured.label')}>
            {/* The tint carries `!` because .glass-card sets `background` as plain
                CSS after @tailwind utilities and would otherwise win on source
                order — the same reason the list rows flag their borders. */}
            <Link
                to={`/event/${event.id}`}
                className="group relative block cursor-pointer glass-card !bg-indigo-50/80 dark:!bg-indigo-950/50 !shadow-glass-lg ring-1 ring-indigo-500/25 dark:ring-indigo-400/25"
            >
                <div className="relative p-5 sm:p-7">
                    {/* Nothing above the name: no eyebrow, no badge, no caption.
                        Share is the single action, so it rides alongside the title
                        rather than claiming a row of its own. */}
                    <div className="flex items-start justify-between gap-3">
                        {/* Name — display sized, well past anything in the grid */}
                        <h2 className="text-2xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight tracking-tight group-hover:underline decoration-indigo-400 decoration-2 underline-offset-4">
                            {event.meta.name}
                        </h2>

                        <button
                            onClick={handleShareClick}
                            className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
                                isCopied
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                                    : 'bg-gray-100/80 dark:bg-gray-700/60 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                            }`}
                            title={isCopied ? t('myEvents.eventCard.copied') : t('myEvents.eventCard.share')}
                            aria-label={isCopied ? t('myEvents.eventCard.copied') : t('myEvents.eventCard.share')}
                        >
                            <FontAwesomeIcon icon={isCopied ? faCheck : faShare} className="text-sm" />
                        </button>
                    </div>

                    {event.meta.description && (
                        <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 line-clamp-2 leading-relaxed max-w-2xl">
                            {event.meta.description}
                        </p>
                    )}

                    {/* Stats */}
                    <div className="flex items-center flex-wrap gap-x-2.5 gap-y-1.5 text-sm text-gray-500 dark:text-gray-400 mt-4">
                        <span className="flex items-center whitespace-nowrap">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-1.5 text-gray-400 dark:text-gray-500" />
                            {new Date(event.meta.createdAt).toLocaleDateString(i18n.language || 'en', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                            })}
                        </span>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="flex items-center whitespace-nowrap">
                            <FontAwesomeIcon icon={faUsers} className="mr-1.5 text-gray-400 dark:text-gray-500" />
                            {t('myEvents.featured.participants', { count: participantIds.length })}
                        </span>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="flex items-center whitespace-nowrap">
                            <FontAwesomeIcon icon={faMoneyBillWave} className="mr-1.5 text-gray-400 dark:text-gray-500" />
                            {expenseCount === 0
                                ? t('myEvents.featured.noExpenses')
                                : t('myEvents.featured.expenses', { count: expenseCount })}
                            {total > 0 && (
                                <span className="ml-1.5 font-bold text-gray-900 dark:text-white">
                                    {formatCurrency(total)}
                                </span>
                            )}
                        </span>
                    </div>

                    {/* Participants */}
                    {sortedParticipantIds.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-4">
                            {sortedParticipantIds.slice(0, 6).map((participantId) => {
                                const isParticipantOwner = participantId === event.meta.createdBy;
                                return (
                                    <span
                                        key={participantId}
                                        className={`px-2 py-0.5 text-xs rounded-md inline-flex items-center gap-1 border ${
                                            isParticipantOwner
                                                ? 'bg-white/80 dark:bg-gray-700/80 text-gray-900 dark:text-white font-bold border-gray-200 dark:border-gray-600'
                                                : 'bg-gray-100/70 dark:bg-gray-700/40 text-gray-600 dark:text-gray-300 border-gray-200/70 dark:border-gray-600/50'
                                        }`}
                                    >
                                        {isParticipantOwner && (
                                            <FontAwesomeIcon icon={faCrown} className="text-[9px] text-amber-500 dark:text-amber-400" />
                                        )}
                                        {participantNames[participantId] || participantId}
                                    </span>
                                );
                            })}
                            {participantIds.length > 6 && (
                                <span className="px-2 py-0.5 text-xs rounded-md bg-gray-100/70 dark:bg-gray-700/40 text-gray-600 dark:text-gray-300 border border-gray-200/70 dark:border-gray-600/50">
                                    {t('myEvents.eventCard.more', { count: participantIds.length - 6 })}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </Link>
        </section>
    );
};

export default FeaturedEventCard;
